import type { RemoteKey } from '@multiview/shared';

/**
 * Game controllers (Steam Deck, Xbox, PlayStation) as one more remote. The
 * Gamepad API has no events for buttons, so pads are polled -- once per frame,
 * and only while one is connected. Buttons follow the browser's "standard"
 * mapping, which Steam Input's virtual pad and every common controller use.
 */

/** Standard mapping button index -> remote key. */
const BUTTONS: Record<number, RemoteKey> = {
  0: 'Ok', // A / Cross
  1: 'Back', // B / Circle
  2: 'Info', // X / Square: match facts
  3: 'Mute', // Y / Triangle
  4: 'ChannelDown', // LB: previous angle
  5: 'ChannelUp', // RB: next angle
  6: 'Yellow', // LT: picture earlier (sync)
  7: 'Blue', // RT: picture later (sync)
  8: 'Red', // View / Select: status overlay
  9: 'Menu', // Menu / Start
  12: 'Up', 13: 'Down', 14: 'Left', 15: 'Right',
};

/** Held arrows repeat like a keyboard, so long lists scroll. */
const REPEATING = new Set<RemoteKey>(['Up', 'Down', 'Left', 'Right']);
const REPEAT_DELAY_MS = 400;
const REPEAT_EVERY_MS = 130;
/** Stick travel that counts as a press, with a lower release point so it does not chatter. */
const STICK_ON = 0.6;
const STICK_OFF = 0.35;

export function attachGamepads(press: (key: RemoteKey) => void): () => void {
  if (typeof navigator.getGamepads !== 'function') return () => {};

  /** pad index -> key -> when it went down / last repeated. */
  const held = new Map<number, Map<RemoteKey, { since: number; last: number }>>();
  let frame: number | null = null;

  const poll = (now: number) => {
    frame = null;
    let any = false;
    for (const pad of navigator.getGamepads()) {
      if (!pad?.connected) continue;
      any = true;
      const down = new Set<RemoteKey>();
      pad.buttons.forEach((b, i) => {
        const key = BUTTONS[i];
        if (key && (b.pressed || b.value > 0.5)) down.add(key);
      });
      stick(pad.axes[0] ?? 0, pad.axes[1] ?? 0, held.get(pad.index), down);

      const state = held.get(pad.index) ?? new Map();
      held.set(pad.index, state);
      for (const key of down) {
        const h = state.get(key);
        if (!h) {
          state.set(key, { since: now, last: now });
          press(key);
        } else if (REPEATING.has(key) && now - h.since >= REPEAT_DELAY_MS && now - h.last >= REPEAT_EVERY_MS) {
          h.last = now;
          press(key);
        }
      }
      for (const key of [...state.keys()]) if (!down.has(key)) state.delete(key);
    }
    if (any) frame = requestAnimationFrame(poll);
  };

  const start = () => {
    if (frame === null) frame = requestAnimationFrame(poll);
  };
  const onDisconnect = (e: GamepadEvent) => held.delete(e.gamepad.index);

  window.addEventListener('gamepadconnected', start);
  window.addEventListener('gamepaddisconnected', onDisconnect);
  // A pad already connected before the page loaded shows up on its first poll.
  start();

  return () => {
    window.removeEventListener('gamepadconnected', start);
    window.removeEventListener('gamepaddisconnected', onDisconnect);
    if (frame !== null) cancelAnimationFrame(frame);
    frame = null;
  };
}

/** The left stick as arrows, with hysteresis against the previous state. */
function stick(x: number, y: number, prev: Map<RemoteKey, unknown> | undefined, down: Set<RemoteKey>): void {
  const on = (key: RemoteKey, value: number) => value >= (prev?.has(key) ? STICK_OFF : STICK_ON);
  // Only the dominant axis, so a diagonal push does not move twice.
  if (Math.abs(x) > Math.abs(y)) {
    if (on('Right', x)) down.add('Right');
    else if (on('Left', -x)) down.add('Left');
  } else {
    if (on('Down', y)) down.add('Down');
    else if (on('Up', -y)) down.add('Up');
  }
}
